import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import { Badge } from '@/common/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { ScrollArea, ScrollBar } from '@/common/ui/scroll-area'
import { Skeleton } from '@/common/ui/skeleton'
import { LOG_TYPE_LABELS } from '../../constants'
import {
	LineAutoMessageLogModel,
	LineMessageLogMessageModel,
} from '../../types'
import { MessageListPreview } from '@/modules/chat/pages/ChatSettingsPage/components'
import type { MessageContentInput } from '@/modules/chat/pages/ChatSettingsPage/types'
import { ChatMessageType } from '@/schema'

interface LineMessageLogDetailProps {
	log: LineAutoMessageLogModel
	groupedMessages?: LineMessageLogMessageModel[]
	totalMessages?: number
	totalChatRooms?: number
	chatRoomIds?: string[]
	isLoading?: boolean
}

// Transform LineMessageLogMessageModel to MessageContentInput for reuse
function transformToMessageContentInput(
	message: LineMessageLogMessageModel,
): MessageContentInput {
	return {
		id: message.id,
		order: 0,
		messageType: message.messageType as ChatMessageType,
		text: message.text ?? undefined,
		stickerPackageId: message.stickerPackageId ?? undefined,
		stickerId: message.stickerId ?? undefined,
		imageUrl: message.imageOriginalUrl ?? message.imagePreviewUrl ?? undefined,
		videoUrl: message.videoOriginalUrl ?? undefined,
		videoPreviewUrl: message.videoPreviewUrl ?? undefined,
		flexAltText: message.flexAltText ?? undefined,
		flexContents: message.flexContents as Record<string, unknown> | undefined,
	}
}

export default function LineMessageLogDetail({
	log,
	groupedMessages = [],
	totalChatRooms = 0,
	chatRoomIds = [],
	isLoading,
}: LineMessageLogDetailProps) {
	// Transform messages for preview component
	const previewMessages = groupedMessages.map(transformToMessageContentInput)

	return (
		<div className="space-y-6">
			{/* Log Info Card */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<span>รายละเอียด Log</span>
						<Badge
							variant={
								log.logType === 'BROADCAST'
									? 'default'
									: log.logType === 'AUTO_REPLY'
										? 'secondary'
										: 'outline'
							}
						>
							{LOG_TYPE_LABELS[log.logType] || log.logType}
						</Badge>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-6">
					<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
						<div>
							<p className="text-sm text-muted-foreground">วันที่</p>
							<p className="font-medium">
								{format(new Date(log.createdAt), 'dd MMMM yyyy HH:mm:ss', {
									locale: th,
								})}
							</p>
						</div>
						<div>
							<p className="text-sm text-muted-foreground">เป้าหมาย</p>
							<p className="font-medium">
								{log.totalTargeted.toLocaleString()}
							</p>
						</div>
						<div>
							<p className="text-sm text-muted-foreground">สำเร็จ</p>
							<p className="font-medium text-green-600">
								{log.successCount.toLocaleString()}
							</p>
						</div>
						<div>
							<p className="text-sm text-muted-foreground">ล้มเหลว</p>
							<p className="font-medium text-red-600">
								{log.failedCount.toLocaleString()}
							</p>
						</div>
					</div>

					{log.xLineRequestId && (
						<div>
							<p className="text-sm text-muted-foreground">LINE Request ID</p>
							<p className="font-mono text-sm">{log.xLineRequestId}</p>
						</div>
					)}

					{/* Chat Rooms Summary */}
					{chatRoomIds.length > 0 && (
						<div>
							<div className="mb-2 flex items-center justify-between">
								<p className="text-sm text-muted-foreground">
									ห้องแชทที่ได้รับข้อความ
								</p>
								<span className="text-xs text-muted-foreground">
									{totalChatRooms > chatRoomIds.length
										? `แสดง ${chatRoomIds.length} จาก ${totalChatRooms.toLocaleString()} ห้อง`
										: `${chatRoomIds.length} ห้อง`}
								</span>
							</div>
							<ScrollArea className="max-h-[120px]">
								<div className="flex flex-wrap gap-2">
									{chatRoomIds.map((roomId) => (
										<Badge
											key={roomId}
											variant="outline"
											className="font-mono text-xs"
										>
											{roomId.substring(0, 8)}...
										</Badge>
									))}
								</div>
								<ScrollBar orientation="vertical" />
							</ScrollArea>
						</div>
					)}
				</CardContent>
			</Card>

			{/* Messages Preview Card */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center justify-between">
						<span>ข้อความที่ส่ง</span>
						{!isLoading && (
							<span className="text-sm font-normal text-muted-foreground">
								{groupedMessages.length} ข้อความ
							</span>
						)}
					</CardTitle>
				</CardHeader>
				<CardContent>
					{isLoading ? (
						<div className="space-y-3">
							{[1, 2, 3].map((i) => (
								<Skeleton key={i} className="h-24 w-full" />
							))}
						</div>
					) : previewMessages.length > 0 ? (
						<>
							<div className="pr-4">
								<MessageListPreview messages={previewMessages} />
							</div>
							<ScrollBar orientation="vertical" />
						</>
					) : (
						<div className="flex h-32 items-center justify-center text-muted-foreground">
							ไม่พบข้อความ
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	)
}
